import { MastershopSyncStatus } from './MastershopSyncStatus';
import React, { useState, useEffect } from 'react';
import { Trophy, Gift, RefreshCw, Sparkles, TrendingUp, AlertCircle, ExternalLink, Store, Settings, Key } from 'lucide-react';
import { Button } from '../ui/button';
import { mastershopHelpers, getCurrentYearMonth, formatMonthName } from '../../helpers/mastershopHelpers';
import { SellerRevenueMonthly, SellerStoreIntegration, REVENUE_MILESTONES } from '../../types/sellerIntegration';
import { MilestoneRewardModal } from './MilestoneRewardModal';
import { ManageStoresModal } from '../settings/ManageStoresModal';
import { useToast } from '../../contexts/ToastContext';

interface SellerRevenueProgressBarProps {
  userId?: string;
  targetMonth?: string;
  onOpenIntegrations?: () => void;
  onOpenLeaderboard?: () => void;
  compact?: boolean;
  variant?: 'full' | 'compact' | 'topbar';
}

export const SellerRevenueProgressBar: React.FC<SellerRevenueProgressBarProps> = ({
  userId,
  targetMonth,
  onOpenIntegrations,
  onOpenLeaderboard,
  compact = false,
  variant,
}) => {
  const isTopbar = variant === 'topbar';
  const isCompact = variant === 'compact' || (!variant && compact);
  const activeMonth = targetMonth || getCurrentYearMonth();
  const monthDisplay = formatMonthName(activeMonth);
  const { showToast } = useToast();
  const [revenueData, setRevenueData] = useState<SellerRevenueMonthly | null>(null);
  const [stores, setStores] = useState<SellerStoreIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [isManageStoresOpen, setIsManageStoresOpen] = useState(false);
  const [unclaimedMilestoneId, setUnclaimedMilestoneId] = useState<string | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);

  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId, activeMonth]);

  const loadData = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const [storesRes, revRes] = await Promise.all([
        mastershopHelpers.getUserStores(userId),
        mastershopHelpers.getMonthlyRevenue(userId, activeMonth)
      ]);

      setStores(storesRes.data || []);
      if (revRes.data) {
        setRevenueData(revRes.data);
        checkUnclaimedMilestones(revRes.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados de faturamento:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkUnclaimedMilestones = (rev: SellerRevenueMonthly) => {
    const total = Number(rev.total_revenue || 0);
    const claimed = rev.claimed_milestones || [];

    // Checar marcos na ordem (100k, 250k, 500k, 1m)
    for (const milestone of REVENUE_MILESTONES) {
      if (total >= milestone.target && !claimed.includes(milestone.id)) {
        setUnclaimedMilestoneId(milestone.id);
        return;
      }
    }
    setUnclaimedMilestoneId(null);
  };

  const handleOpenStoreManager = () => {
    if (onOpenIntegrations) {
      onOpenIntegrations();
    } else {
      setIsManageStoresOpen(true);
    }
  };

  const handleSync = async () => {
    if (!userId) return;
    if (stores.length === 0) {
      showToast({
        type: 'info',
        title: 'Cadastre sua Loja',
        message: 'Por favor, adicione sua chave de API da Master Shop para sincronizar.',
      });
      setIsManageStoresOpen(true);
      return;
    }

    try {
      setSyncing(true);
      const result = await mastershopHelpers.syncRevenue(userId, activeMonth);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Sincronização solicitada',
          message: result.data?.queued === false ? 'Já houve uma solicitação recente. Aguarde cinco minutos para solicitar outra.' : 'Processamento em segundo plano. O total será atualizado ao concluir todas as lojas.',
        });
        await loadData();
      } else {
        showToast({
          type: 'error',
          title: 'Falha na Sincronização',
          message: result.error || 'Verifique as chaves de API nas configurações.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message || 'Erro ao sincronizar.',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleClaimReward = async (milestoneId: string) => {
    if (!userId) return;
    try {
      setIsClaiming(true);
      const result = await mastershopHelpers.claimMilestone(userId, milestoneId, activeMonth);
      if (result.success) {
        showToast({
          type: 'success',
          title: 'Recompensa Resgatada! 🎉',
          message: `Sua nova meta foi atualizada para ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(result.nextTarget || 250000)}!`,
        });
        setIsRewardModalOpen(false);
        await loadData();
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Resgatar',
          message: result.error || 'Não foi possível resgatar a recompensa.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message || 'Erro ao processar resgate.',
      });
    } finally {
      setIsClaiming(false);
    }
  };

  const formatCurrency = (amount: number, curr = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: curr === 'COP' ? 'COP' : 'BRL',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const total = Number(revenueData?.total_revenue || 0);
  const target = Number(revenueData?.current_milestone_target || 100000);
  const percentage = Math.min(Math.round((total / target) * 100), 100);
  const currency = revenueData?.currency || 'BRL';
  const storeCount = stores.length || revenueData?.store_count || 0;

  // Se o usuário não estiver logado
  if (!userId) return null;

  // Versão Fixa na Topbar (à direita de Olá, {displayName}!)
  if (isTopbar) {
    if (loading) {
      return (
        <div className="flex items-center gap-2.5 px-3 py-1.5 bg-[#16181f]/90 border border-gray-800 rounded-xl animate-pulse">
          <div className="w-5 h-5 rounded-lg bg-gray-800" />
          <div className="flex flex-col space-y-1">
            <div className="w-16 h-2 bg-gray-800 rounded" />
            <div className="w-24 h-1.5 bg-gray-800 rounded-full" />
          </div>
        </div>
      );
    }

    return (
      <div 
        onClick={handleOpenStoreManager}
        title="Faturamento Master Shop • Clique para ver detalhes e sincronizar"
        className="flex items-center gap-2.5 px-3 py-1.5 bg-[#16181f] hover:bg-[#1a1d26] border border-gray-700/60 hover:border-[#0FFF55]/40 rounded-xl transition-all duration-200 cursor-pointer shadow-sm group select-none"
      >
        <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-[#0FFF55]/10 text-[#0FFF55] border border-[#0FFF55]/20 group-hover:scale-105 transition-transform flex-shrink-0">
          <Trophy className="w-3.5 h-3.5" />
        </div>

        <div className="flex flex-col min-w-[110px] sm:min-w-[130px] max-w-[170px]">
          <div className="flex items-center justify-between text-[11px] font-semibold leading-tight gap-1.5">
            <span className="text-white font-bold tracking-tight truncate">
              {formatCurrency(total, currency)}
            </span>
            <span className="text-[#0FFF55] font-black text-[10px] flex-shrink-0">
              {percentage}%
            </span>
          </div>

          <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden mt-1 border border-gray-700/50">
            <div 
              className="bg-gradient-to-r from-[#0dd149] via-[#0FFF55] to-[#8aff95] h-full rounded-full transition-all duration-500 shadow-sm shadow-[#0FFF55]/50" 
              style={{ width: `${Math.max(percentage, 3)}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-[9px] text-gray-400 mt-0.5 leading-none">
            <span className="truncate">Meta: {formatCurrency(target, currency)}</span>
          </div>
        </div>

        {unclaimedMilestoneId && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsRewardModalOpen(true);
            }}
            className="bg-gradient-to-r from-yellow-500 to-amber-600 text-[#121316] font-black text-[10px] px-2 py-1 rounded-lg flex items-center gap-1 animate-pulse shadow-md ml-0.5 flex-shrink-0"
            title="Resgatar Recompensa de Meta"
          >
            <Gift className="w-3 h-3" />
            <span className="hidden xl:inline">Resgatar</span>
          </button>
        )}

        {unclaimedMilestoneId && (
          <MilestoneRewardModal
            isOpen={isRewardModalOpen}
            onClose={() => setIsRewardModalOpen(false)}
            milestoneId={unclaimedMilestoneId}
            totalRevenue={total}
            currency={currency}
            onClaim={handleClaimReward}
            isClaiming={isClaiming}
          />
        )}
      </div>
    );
  }

  // Versão compacta para Dropdown Menu do Usuário
  if (isCompact) {
    return (
      <div className="bg-gradient-to-br from-[#1c1f26] to-[#16181d] border border-gray-700/60 rounded-xl p-3 space-y-2.5 shadow-lg">
        <div className="flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-1.5 min-w-0">
            <Trophy className="w-3.5 h-3.5 text-[#0FFF55] flex-shrink-0" />
            <span className="text-gray-300 font-semibold truncate">Faturamento • {monthDisplay}</span>
          </div>
          <span className="text-[#0FFF55] font-black text-xs flex-shrink-0 whitespace-nowrap">
            {percentage}% da Meta
          </span>
        </div>

        {/* Barra de Progresso */}
        <div className="w-full bg-gray-800 rounded-full h-2.5 overflow-hidden p-0.5 border border-gray-700/50">
          <div 
            className="bg-gradient-to-r from-[#0dd149] via-[#0FFF55] to-[#8aff95] h-full rounded-full transition-all duration-500 shadow-sm shadow-[#0FFF55]/50" 
            style={{ width: `${Math.max(percentage, 3)}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-[11px] text-gray-400">
          <span>{formatCurrency(total, currency)}</span>
          <span className="text-gray-500 font-medium">Meta: {formatCurrency(target, currency)}</span>
        </div>

        {unclaimedMilestoneId && (
          <button
            onClick={() => setIsRewardModalOpen(true)}
            className="w-full bg-gradient-to-r from-yellow-500 to-amber-600 text-[#121316] font-extrabold text-[11px] py-1 rounded-lg flex items-center justify-center gap-1 animate-pulse shadow-md"
          >
            <Gift className="w-3 h-3" />
            Resgatar Recompensa 100k!
          </button>
        )}

        {unclaimedMilestoneId && (
          <MilestoneRewardModal
            isOpen={isRewardModalOpen}
            onClose={() => setIsRewardModalOpen(false)}
            milestoneId={unclaimedMilestoneId}
            totalRevenue={total}
            currency={currency}
            onClaim={handleClaimReward}
            isClaiming={isClaiming}
          />
        )}
      </div>
    );
  }

  // Versão Completa (Dashboard / Home / Perfil)
  return (
    <div className="relative bg-gradient-to-br from-[#1a1d24] via-[#16181f] to-[#121418] border border-gray-700/60 rounded-3xl p-6 sm:p-8 shadow-2xl overflow-hidden mb-8">
      <MastershopSyncStatus month={activeMonth} userId={userId} onUpdate={loadData} />
      {/* Luz ambiente de fundo */}
      <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#0FFF55]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#0FFF55]/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header do Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 bg-gradient-to-br from-[#0FFF55]/20 to-[#0dd149]/10 border border-[#0FFF55]/40 rounded-2xl flex items-center justify-center shadow-lg shadow-[#0FFF55]/10">
            <Trophy className="w-6 h-6 text-[#0FFF55]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                Faturamento Mensal • {monthDisplay}
              </h2>
              <span className="bg-[#0FFF55]/20 text-[#0FFF55] text-xs font-black px-2.5 py-0.5 rounded-full border border-[#0FFF55]/30">
                AO VIVO
              </span>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm">
              Sincronização direta com a <strong className="text-gray-200">Master Shop</strong>. Escale suas vendas e suba de nível!
            </p>
          </div>
        </div>

        {/* Ações Rápidas */}
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          {onOpenLeaderboard && (
            <Button
              onClick={onOpenLeaderboard}
              variant="outline"
              size="sm"
              className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800/80 rounded-xl text-xs flex items-center gap-1.5"
            >
              <TrendingUp className="w-3.5 h-3.5 text-[#0FFF55]" />
              Ver Ranking
            </Button>
          )}

          <Button
            onClick={() => setIsManageStoresOpen(true)}
            variant="outline"
            size="sm"
            className="border-[#0FFF55]/30 text-[#0FFF55] hover:bg-[#0FFF55]/10 rounded-xl text-xs flex items-center gap-1.5"
          >
            <Key className="w-3.5 h-3.5" />
            Configurar Chaves ({storeCount})
          </Button>

          <Button
            onClick={handleSync}
            disabled={syncing || loading}
            size="sm"
            className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] hover:from-[#0dd149] hover:to-[#0FFF55] text-[#121316] font-black rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-[#0FFF55]/20 transition-transform active:scale-95"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </Button>
        </div>
      </div>

      {/* Se não conectou a chave */}
      {stores.length === 0 ? (
        <div className="p-5 bg-[#0FFF55]/10 border border-[#0FFF55]/30 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[#0FFF55]/20 border border-[#0FFF55]/30 rounded-xl flex-shrink-0">
              <Key className="w-5 h-5 text-[#0FFF55]" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">Nenhuma Chave da Master Shop Conectada</h4>
              <p className="text-xs text-gray-400">
                Cadastre sua ApiKey para ativar a sincronização automática e computar suas vendas na régua.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setIsManageStoresOpen(true)}
            size="sm"
            className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] hover:from-[#0dd149] hover:to-[#0FFF55] text-[#121316] font-black rounded-xl text-xs flex-shrink-0 shadow-md shadow-[#0FFF55]/20 transition-transform active:scale-95"
          >
            <Key className="w-3.5 h-3.5 mr-1.5" />
            Cadastrar Chave de API
          </Button>
        </div>
      ) : (
        /* Régua de Progresso do Vendedor */
        <div className="space-y-6">
          {/* Métricas Principais */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-[#1f232b]/60 border border-gray-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-400 font-medium">Faturamento Atual</span>
                {storeCount > 1 && (
                  <span className="text-[10px] bg-[#0FFF55]/10 text-[#0FFF55] font-bold px-1.5 py-0.5 rounded border border-[#0FFF55]/20 flex items-center gap-1">
                    <Store className="w-2.5 h-2.5" /> {storeCount} Lojas
                  </span>
                )}
              </div>
              <span className="text-2xl sm:text-3xl font-black text-[#0FFF55] tracking-tight block">
                {formatCurrency(total, currency)}
              </span>
              {Number(revenueData?.revenue_cop || 0) > 0 && (
                <span className="text-[10px] text-gray-500 font-medium block mt-0.5">
                  Original: {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(revenueData?.revenue_cop || 0)}
                </span>
              )}
            </div>

            <div className="bg-[#1f232b]/60 border border-gray-800 rounded-2xl p-4">
              <span className="text-xs text-gray-400 font-medium block mb-1">Meta Atual do Mês</span>
              <span className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                {formatCurrency(target, currency)}
              </span>
            </div>

            <div className="col-span-2 sm:col-span-1 bg-[#1f232b]/60 border border-gray-800 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <span className="text-xs text-gray-400 font-medium block mb-1">Conquista</span>
                <span className="text-sm font-extrabold text-[#0FFF55] bg-[#0FFF55]/10 border border-[#0FFF55]/30 px-2.5 py-1 rounded-lg inline-block">
                  {total >= 1000000 ? '💎 Clube do Milhão' : total >= 500000 ? '👑 Elite 500k' : total >= 250000 ? '⚡ Elite 250k' : total >= 100000 ? '🚀 Vendedor 100k' : '🌱 Rumo aos 100k'}
                </span>
              </div>
              <span className="text-2xl font-black text-gray-300">
                {percentage}%
              </span>
            </div>
          </div>

          {/* Barra de Progresso com Marcadores da Régua */}
          <div className="space-y-2">
            <div className="relative w-full bg-gray-950 rounded-full h-5 p-1 border border-gray-700/80 shadow-inner">
              <div
                className="bg-gradient-to-r from-[#0dd149] via-[#0FFF55] to-[#8aff95] h-full rounded-full transition-all duration-700 relative shadow-lg shadow-[#0FFF55]/40"
                style={{ width: `${Math.max(percentage, 2)}%` }}
              >
                {/* Efeito Glow na ponta da barra */}
                <div className="absolute right-0 top-0 bottom-0 w-3 bg-white rounded-full opacity-80 animate-pulse" />
              </div>
            </div>

            {/* Marcadores da Régua */}
            <div className="flex justify-between text-[11px] font-semibold text-gray-400 px-1 pt-1">
              <span>{formatCurrency(0, currency)}</span>
              <span>{formatCurrency(target * 0.25, currency)}</span>
              <span>{formatCurrency(target * 0.5, currency)}</span>
              <span>{formatCurrency(target * 0.75, currency)}</span>
              <span className="text-[#0FFF55] font-black">{formatCurrency(target, currency)} 🎯</span>
            </div>
          </div>

          {/* Banner de Celebração de Meta Atingida / Presente Resgatável */}
          {unclaimedMilestoneId && (
            <div className="p-4 bg-gradient-to-r from-yellow-500/20 via-amber-500/20 to-yellow-500/10 border-2 border-yellow-400/80 rounded-2xl flex items-center justify-between gap-4 shadow-xl shadow-yellow-500/10 animate-pulse">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 bg-gradient-to-tr from-yellow-400 to-amber-300 rounded-2xl flex items-center justify-center text-[#121316] font-black shadow-lg shadow-yellow-400/40">
                  <Gift className="w-6 h-6 animate-bounce" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-base font-black text-yellow-300">
                      Parabéns! Meta de {unclaimedMilestoneId.toUpperCase()} Atingida!
                    </h4>
                    <Sparkles className="w-4 h-4 text-yellow-300" />
                  </div>
                  <p className="text-xs text-yellow-100/90">
                    Você desbloqueou uma nova conquista e pode subir sua régua para o próximo marco!
                  </p>
                </div>
              </div>

              <Button
                onClick={() => setIsRewardModalOpen(true)}
                className="bg-yellow-400 hover:bg-yellow-300 text-[#121316] font-black text-xs sm:text-sm px-4 py-2.5 rounded-xl shadow-lg shadow-yellow-400/30 flex-shrink-0 transition-transform hover:scale-105"
              >
                Resgatar Conquista 🎁
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Modal de Confetes e Resgate */}
      {unclaimedMilestoneId && (
        <MilestoneRewardModal
          isOpen={isRewardModalOpen}
          onClose={() => setIsRewardModalOpen(false)}
          milestoneId={unclaimedMilestoneId}
          totalRevenue={total}
          currency={currency}
          onClaim={handleClaimReward}
          isClaiming={isClaiming}
        />
      )}

      {/* Modal de Gerenciamento de Chaves / Lojas */}
      {userId && (
        <ManageStoresModal
          isOpen={isManageStoresOpen}
          onClose={() => setIsManageStoresOpen(false)}
          userId={userId}
          onStoresUpdated={loadData}
        />
      )}
    </div>
  );
};
